import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Plus, Lock, Copy, BookmarkPlus, X, Calendar, CloudUpload, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysIso(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtShort(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function dayLabel(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
}
const emptyMeal = () => ({ time: "", name: "New meal", items: [""], notes: "" });

export default function DietitianClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [duration, setDuration] = useState(7);
  const [startDate, setStartDate] = useState(todayIso());
  const [schedule, setSchedule] = useState([]);
  const [savingAll, setSavingAll] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);

  const loadClient = useCallback(async () => {
    try {
      const r = await api.get(`/app/dietitian/clients/${id}`);
      setClient(r.data.client);
    } catch { toast.error("Client not found"); }
  }, [id]);

  const loadSchedule = useCallback(async () => {
    try {
      const r = await api.get(`/app/dietitian/clients/${id}/schedule`, {
        params: { start: startDate, days: duration },
      });
      setSchedule((r.data.schedule || []).map((d) => ({ ...d, _dirty: false, _collapsed: false })));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [id, startDate, duration]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get("/app/templates");
      setTemplates(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadClient(); loadTemplates(); }, [loadClient, loadTemplates]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // Patch a day's meals/notes by index — marks it dirty
  const patchDay = (idx, patch) => {
    setSchedule((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch, _dirty: true, empty: false } : d)));
  };
  const toggleCollapse = (idx) => {
    setSchedule((prev) => prev.map((d, i) => (i === idx ? { ...d, _collapsed: !d._collapsed } : d)));
  };

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    const tmpl = templates.find((t) => t.id === templateId);
    if (!tmpl) return;
    const tt = tmpl.type || "single";
    const msg = tt === "weekly"
      ? `Apply 7-day template "${tmpl.name}" starting ${fmtShort(startDate)}?`
      : `Apply "${tmpl.name}" to all ${duration} days starting ${fmtShort(startDate)}?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/app/dietitian/clients/${id}/schedule/apply-template`, {
        template_id: templateId, start_date: startDate, duration,
      });
      toast.success(`Template applied — ${r.data.written} days saved` + (r.data.skipped_locked ? `, ${r.data.skipped_locked} locked skipped` : ""));
      await loadSchedule();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const dirtyDays = useMemo(() => schedule.filter((d) => d._dirty && !d.locked), [schedule]);

  const saveAll = async () => {
    if (!dirtyDays.length) { toast.info("No changes to save"); return; }
    setSavingAll(true);
    try {
      const payload = {
        days: dirtyDays.map((d) => ({
          date: d.date,
          meals: (d.meals || []).map((m) => ({ ...m, items: (m.items || []).filter(Boolean) })),
          notes: d.notes || null,
        })),
      };
      const r = await api.put(`/app/dietitian/clients/${id}/schedule/bulk`, payload);
      toast.success(`Saved ${r.data.written} day${r.data.written === 1 ? "" : "s"}` + (r.data.skipped_locked?.length ? ` · ${r.data.skipped_locked.length} locked skipped` : ""));
      await loadSchedule();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSavingAll(false); }
  };

  const deleteAll = async () => {
    const futureCount = schedule.filter((d) => !d.locked).length;
    if (!futureCount) { toast.info("Nothing to delete"); return; }
    if (!window.confirm(`Delete all ${futureCount} unlocked days in this window? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/app/dietitian/clients/${id}/schedule/clear-window`, {
        params: { start: startDate, days: duration },
      });
      toast.success(`Deleted ${r.data.deleted} day${r.data.deleted === 1 ? "" : "s"}`);
      await loadSchedule();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const daysWithContent = useMemo(() => {
    const t = todayIso();
    return schedule.filter((d) => d.date >= t && (d.meals || []).length > 0).length;
  }, [schedule]);

  if (!client) return <div className="pt-8 text-center">Loading…</div>;

  return (
    <div>
      <Link to="/app" className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "var(--primary)" }}>
        <ArrowLeft size={12} /> Back to clients
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg" style={{ background: "var(--yellow)", color: "var(--primary)" }}>
          {(client.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-2xl leading-tight" style={{ color: "var(--primary)" }}>{client.name}</h1>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{client.phone} · {client.city || "—"}</div>
        </div>
      </div>

      {/* Plan window + bulk controls */}
      <div className="mt-6 card-soft p-4">
        <div className="section-eyebrow flex items-center gap-2"><Calendar size={12} /> Plan window</div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 rounded-full p-1" style={{ background: "var(--bg-paper)" }}>
            {[7, 30].map((d) => (
              <button
                key={d}
                data-testid={`duration-${d}`}
                onClick={() => setDuration(d)}
                className="px-3 py-1 text-xs font-bold rounded-full"
                style={{ background: duration === d ? "var(--primary)" : "transparent", color: duration === d ? "var(--bg)" : "var(--primary)" }}
              >{d}-day</button>
            ))}
          </div>
          <div className="flex-1 min-w-[140px]">
            <Input
              data-testid="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <select
            data-testid="template-picker"
            onChange={(e) => { if (e.target.value) { applyTemplate(e.target.value); e.target.value = ""; } }}
            defaultValue=""
            className="text-xs h-9 rounded-full px-3 border-2 bg-transparent font-bold flex-1"
            style={{ borderColor: "var(--border-strong)", color: "var(--primary)" }}
          >
            <option value="">Apply template…</option>
            <optgroup label="7-day templates">
              {templates.filter((t) => t.type === "weekly").map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </optgroup>
            <optgroup label="Single-day templates">
              {templates.filter((t) => (t.type || "single") === "single").map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </optgroup>
          </select>
          <button
            data-testid="save-week-as-template"
            onClick={() => setShowSaveAsTemplate(true)}
            className="btn-ghost text-xs"
            title="Save current window as a 7-day template"
          >
            <BookmarkPlus size={12} /> Save as template
          </button>
        </div>
        <div className="mt-2 text-xs" style={{ color: "var(--text-secondary)" }}>
          <b style={{ color: "var(--primary)" }}>{daysWithContent}</b> of {duration} days have meals
        </div>
      </div>

      {/* Sticky bulk-action bar */}
      <div data-testid="bulk-bar" className="sticky top-14 z-20 mt-4 p-3 rounded-xl flex items-center gap-2 shadow-lg" style={{ background: "var(--primary)", color: "var(--bg)" }}>
        <span className="text-xs font-bold flex-1">
          {dirtyDays.length > 0
            ? <>{dirtyDays.length} day{dirtyDays.length === 1 ? "" : "s"} unsaved</>
            : "All synced"}
        </span>
        <button
          data-testid="delete-all-days"
          onClick={deleteAll}
          className="text-xs font-bold rounded-full px-3 py-2 flex items-center gap-1.5"
          style={{ background: "transparent", border: "1.5px solid var(--yellow)", color: "var(--yellow)" }}
        >
          <Trash2 size={13} /> Delete all
        </button>
        <button
          data-testid="save-all-days"
          disabled={savingAll || dirtyDays.length === 0}
          onClick={saveAll}
          className="text-xs font-bold rounded-full px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
          style={{ background: "var(--yellow)", color: "var(--primary)" }}
        >
          <CloudUpload size={14} /> {savingAll ? "Saving…" : "Save all"}
        </button>
      </div>

      {/* All days — stacked */}
      <div className={`mt-4 ${duration === 30 ? "overflow-x-visible" : ""}`}>
        <div className="section-eyebrow">
          {duration === 7 ? "Full 7-day plan" : "Full 30-day plan (scroll to see all)"}
        </div>
        <div className="mt-3 space-y-4">
          {schedule.map((d, idx) => (
            <DayEditor
              key={d.date}
              idx={idx}
              day={d}
              onPatch={(patch) => patchDay(idx, patch)}
              onToggle={() => toggleCollapse(idx)}
            />
          ))}
        </div>
      </div>

      {showSaveAsTemplate && (
        <SaveWeekAsTemplateModal
          clientId={id}
          startDate={startDate}
          onClose={() => setShowSaveAsTemplate(false)}
          onSaved={() => { setShowSaveAsTemplate(false); loadTemplates(); }}
        />
      )}
    </div>
  );
}

function DayEditor({ idx, day, onPatch, onToggle }) {
  const isLocked = !!day.locked;
  const meals = day.meals || [];
  const notes = day.notes || "";

  const updateMeal = (i, patch) => onPatch({ meals: meals.map((m, k) => (k === i ? { ...m, ...patch } : m)) });
  const removeMeal = (i) => onPatch({ meals: meals.filter((_, k) => k !== i) });
  const addMeal = () => onPatch({ meals: [...meals, emptyMeal()] });
  const updateItem = (mi, ii, val) => updateMeal(mi, { items: meals[mi].items.map((it, k) => (k === ii ? val : it)) });
  const addItem = (mi) => updateMeal(mi, { items: [...meals[mi].items, ""] });
  const removeItem = (mi, ii) => updateMeal(mi, { items: meals[mi].items.filter((_, k) => k !== ii) });

  return (
    <div
      data-testid={`day-${idx}`}
      className="card-soft p-4"
      style={{ opacity: isLocked ? 0.75 : 1, borderLeft: day._dirty ? "4px solid var(--red)" : undefined }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
            {isLocked && <Lock size={10} className="inline mr-1" />}
            Day {idx + 1} · {dayLabel(day.date)}
            {day.is_today && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--yellow)", color: "var(--primary)" }}>TODAY</span>}
            {day._dirty && !isLocked && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--red)", color: "var(--bg)" }}>UNSAVED</span>}
          </div>
          <div className="font-display text-lg leading-tight mt-0.5" style={{ color: "var(--primary)" }}>{fmtShort(day.date)}</div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {meals.length ? `${meals.length} meal${meals.length === 1 ? "" : "s"}` : "No meals yet"}
          </div>
        </div>
        {day._collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {!day._collapsed && (
        <div className="mt-3">
          {isLocked && (
            <div className="p-3 rounded-xl text-xs flex items-start gap-2 mb-3" style={{ background: "var(--yellow-soft)", border: "1.5px solid var(--yellow-deep)" }}>
              <Lock size={14} className="mt-0.5 flex-shrink-0" style={{ color: "var(--primary)" }} />
              <div style={{ color: "var(--text-secondary)" }}>Past day — locked (read-only).</div>
            </div>
          )}

          <div className="space-y-2.5">
            {meals.map((m, i) => (
              <div key={i} className="p-3 rounded-xl" style={{ background: "var(--bg-paper)" }}>
                <div className="flex gap-2">
                  <Input value={m.time || ""} onChange={(e) => !isLocked && updateMeal(i, { time: e.target.value })} placeholder="Time" className="h-9 rounded-lg w-20 text-sm" disabled={isLocked} />
                  <Input value={m.name || ""} onChange={(e) => !isLocked && updateMeal(i, { name: e.target.value })} placeholder="Meal name" className="h-9 rounded-lg flex-1 text-sm" disabled={isLocked} />
                  {!isLocked && (
                    <button onClick={() => removeMeal(i)} className="text-red-600"><Trash2 size={13} /></button>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  {(m.items || []).map((it, ii) => (
                    <div key={ii} className="flex gap-2">
                      <Input value={it} onChange={(e) => !isLocked && updateItem(i, ii, e.target.value)} placeholder="Item…" className="h-8 rounded-lg text-sm" disabled={isLocked} />
                      {!isLocked && <button onClick={() => removeItem(i, ii)} className="text-red-600 text-xs px-1">×</button>}
                    </div>
                  ))}
                  {!isLocked && (
                    <button onClick={() => addItem(i)} className="text-xs font-bold flex items-center gap-1" style={{ color: "var(--red)" }}>
                      <Plus size={10} /> Add item
                    </button>
                  )}
                </div>
                <Input value={m.notes || ""} onChange={(e) => !isLocked && updateMeal(i, { notes: e.target.value })} placeholder="Meal notes (optional)" className="mt-2 h-8 rounded-lg text-sm" disabled={isLocked} />
              </div>
            ))}

            {!isLocked && (
              <button onClick={addMeal} className="btn-ghost w-full text-xs">
                <Plus size={12} /> Add meal to this day
              </button>
            )}
          </div>

          <Input value={notes} onChange={(e) => !isLocked && onPatch({ notes: e.target.value })} placeholder="Overall note for this day" className="mt-3 h-9 rounded-lg text-sm" disabled={isLocked} />
        </div>
      )}
    </div>
  );
}

function SaveWeekAsTemplateModal({ clientId, startDate, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/app/dietitian/clients/${clientId}/schedule/save-as-template`, {
        start_date: startDate,
        name,
        description,
        tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("7-day template created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="w-full max-w-md card-soft p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="section-eyebrow">Save as</div>
            <h3 className="font-display text-2xl mt-1" style={{ color: "var(--primary)" }}>7-day template</h3>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Captures the 7 days starting {fmtShort(startDate)}</p>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <Input required placeholder="Template name *" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" />
          <Input placeholder="Short description" value={description} onChange={(e) => setDescription(e.target.value)} className="h-11 rounded-xl" />
          <Input placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} className="h-11 rounded-xl" />
          <button disabled={loading} className="btn-primary w-full disabled:opacity-60">
            {loading ? "Saving…" : "Save 7-day template"}
          </button>
        </form>
      </div>
    </div>
  );
}
